import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Bell, Trash2, StickyNote, UserCog } from "lucide-react";

const COLORS = [
    { key: "blue", label: "Blue", cls: "bg-ikb" },
    { key: "amber", label: "Amber", cls: "bg-amber-500" },
    { key: "rose", label: "Rose", cls: "bg-rose-500" },
    { key: "emerald", label: "Green", cls: "bg-emerald-500" },
    { key: "grey", label: "Grey", cls: "bg-slate-400" },
];

const REMINDER_OPTIONS = [
    { key: "", label: "No reminder" },
    { key: "0", label: "At time" },
    { key: "15", label: "15 min before" },
    { key: "60", label: "1 hour before" },
    { key: "1440", label: "1 day before" },
];

export default function CalendarNoteDialog({ open, note, onClose, onSaved }) {
    const [form, setForm] = useState(note || {});
    const [saving, setSaving] = useState(false);
    const [assignableUsers, setAssignableUsers] = useState([]);

    useEffect(() => {
        setForm(note || {});
    }, [note]);

    useEffect(() => {
        if (!open) return;
        api.get("/calendar/assignable-users").then(({ data }) => setAssignableUsers(data?.items || [])).catch(() => {});
    }, [open]);

    if (!open || !note) return null;

    const isNew = !note.id;

    const save = async () => {
        if (!form.title?.trim()) { toast.error("Title is required"); return; }
        if (!form.date) { toast.error("Date is required"); return; }
        setSaving(true);
        try {
            const payload = {
                title: form.title.trim(),
                body: form.body || "",
                date: form.date,
                time: form.time || null,
                reminder_minutes_before: form.reminder_minutes_before === "" || form.reminder_minutes_before === null || form.reminder_minutes_before === undefined ? null : Number(form.reminder_minutes_before),
                color: form.color || "blue",
                completed: !!form.completed,
                assigned_to: form.assigned_to || undefined,
            };
            if (isNew) {
                await api.post("/calendar/notes", payload);
                toast.success("Note added");
            } else {
                await api.patch(`/calendar/notes/${note.id}`, payload);
                toast.success("Note updated");
            }
            onSaved?.();
            onClose?.();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(false); }
    };

    const remove = async () => {
        if (!note.id) { onClose?.(); return; }
        if (!confirm("Delete this note?")) return;
        try {
            await api.delete(`/calendar/notes/${note.id}`);
            toast.success("Note deleted");
            onSaved?.(); onClose?.();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
            <DialogContent className="max-w-md rounded-none border border-border" data-testid="cal-note-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display flex items-center gap-2">
                        <StickyNote className="w-5 h-5 text-amber-500" />
                        {isNew ? "New note / reminder" : "Edit note"}
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-3 mt-2">
                    <div>
                        <label className="text-[10px] font-mono uppercase tracking-widest text-foreground/70">Title</label>
                        <Input
                            value={form.title || ""}
                            onChange={(e) => setForm({ ...form, title: e.target.value })}
                            placeholder="Call back the candidate, prep round 3…"
                            className="rounded-none mt-1"
                            data-testid="note-title"
                        />
                    </div>
                    <div>
                        <label className="text-[10px] font-mono uppercase tracking-widest text-foreground/70">Details</label>
                        <Textarea
                            value={form.body || ""}
                            onChange={(e) => setForm({ ...form, body: e.target.value })}
                            placeholder="Free-form notes…"
                            className="rounded-none mt-1 min-h-[80px]"
                            data-testid="note-body"
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <label className="text-[10px] font-mono uppercase tracking-widest text-foreground/70">Date</label>
                            <Input
                                type="date" value={form.date || ""}
                                onChange={(e) => setForm({ ...form, date: e.target.value })}
                                className="rounded-none mt-1"
                                data-testid="note-date"
                            />
                        </div>
                        <div>
                            <label className="text-[10px] font-mono uppercase tracking-widest text-foreground/70">Time (leave empty → all-day)</label>
                            <Input
                                type="time" value={form.time || ""}
                                onChange={(e) => setForm({ ...form, time: e.target.value })}
                                className="rounded-none mt-1"
                                data-testid="note-time"
                            />
                        </div>
                    </div>
                    {!isNew && (
                        <label className="flex items-center gap-2 text-xs cursor-pointer select-none" data-testid="note-completed-row">
                            <input
                                type="checkbox"
                                checked={!!form.completed}
                                onChange={(e) => setForm({ ...form, completed: e.target.checked })}
                                className="w-4 h-4 accent-ikb"
                                data-testid="note-completed"
                            />
                            <span>Mark as completed</span>
                        </label>
                    )}
                    <div>
                        <label className="text-[10px] font-mono uppercase tracking-widest text-foreground/70 flex items-center gap-1"><UserCog className="w-3 h-3" /> Assigned to</label>
                        <select
                            value={form.assigned_to || ""}
                            onChange={(e) => setForm({ ...form, assigned_to: e.target.value || null })}
                            className="w-full h-9 px-2 border border-border bg-white text-sm mt-1"
                            data-testid="note-assignee"
                        >
                            <option value="">Me (creator)</option>
                            {assignableUsers.map((u) => (
                                <option key={u.id} value={u.id}>{u.name || u.email} · {u.role}</option>
                            ))}
                        </select>
                        {!isNew && form.created_by_name && (
                            <div className="text-[10px] font-mono text-foreground/50 mt-1">
                                Created by <b>{form.created_by_name}</b>
                                {form.assigned_to_name && form.assigned_to_name !== form.created_by_name ? ` · currently assigned to ${form.assigned_to_name}` : ""}
                            </div>
                        )}
                    </div>
                    <div>
                        <label className="text-[10px] font-mono uppercase tracking-widest text-foreground/70 flex items-center gap-1"><Bell className="w-3 h-3" /> Reminder</label>
                        <select
                            value={form.reminder_minutes_before === null || form.reminder_minutes_before === undefined ? "" : String(form.reminder_minutes_before)}
                            onChange={(e) => setForm({ ...form, reminder_minutes_before: e.target.value })}
                            className="w-full h-9 px-2 border border-border bg-white text-sm mt-1"
                            data-testid="note-reminder"
                        >
                            {REMINDER_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="text-[10px] font-mono uppercase tracking-widest text-foreground/70">Color</label>
                        <div className="flex items-center gap-2 mt-1">
                            {COLORS.map((c) => (
                                <button
                                    key={c.key}
                                    type="button"
                                    onClick={() => setForm({ ...form, color: c.key })}
                                    className={`w-7 h-7 ${c.cls} border-2 ${form.color === c.key ? "border-foreground" : "border-transparent"}`}
                                    title={c.label}
                                    data-testid={`note-color-${c.key}`}
                                />
                            ))}
                        </div>
                    </div>
                </div>
                <div className="flex justify-between gap-2 mt-4">
                    {!isNew ? (
                        <Button variant="outline" onClick={remove} className="rounded-none text-rose-700 border-rose-300 hover:bg-rose-50" data-testid="note-delete">
                            <Trash2 className="w-4 h-4 mr-1" /> Delete
                        </Button>
                    ) : <span />}
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={onClose} className="rounded-none" data-testid="note-cancel">Cancel</Button>
                        <Button onClick={save} disabled={saving} className="rounded-none bg-ikb hover:bg-[#001f7a] text-white" data-testid="note-save">
                            {saving ? "Saving…" : "Save note"}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
